import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faAngleDown,
  faAngleUp,
  faCalendar,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  Dropdown,
  DropdownOptionLabel,
} from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type {
  TrckGroupAsideGWSelectorFixtureQuery,
  TrckGroupAsideGWSelectorFixtureQueryVariables,
  TrckGroupAsideGWSelectorQuery,
  TrckGroupAsideGWSelectorQueryVariables,
  TrckGroupAsideGWSelector_so5Fixture,
} from './__generated__/index.graphql';

const OptionsWrapper = styled.div`
  @media ${laptopAndAbove} {
    max-height: 220px;
  }
`;

const query = gql`
  query TrckGroupAsideGWSelectorQuery(
    $sport: Sport!
    $eventType: So5FixtureEvent!
    $so5LeaderboardType: [So5LeaderboardType!]!
    $aasmStates: [String!] = ["started", "closed", "opened"]
  ) {
    so5 {
      so5Fixtures(
        sport: $sport
        eventType: $eventType
        aasmStates: $aasmStates
        so5LeaderboardTypes: $so5LeaderboardType
      ) {
        nodes {
          slug
          endDate
          gameWeek
          startDate
          shortDisplayName
        }
      }
    }
  }
` as TypedDocumentNode<
  TrckGroupAsideGWSelectorQuery,
  TrckGroupAsideGWSelectorQueryVariables
>;

const fixtureQuery = gql`
  query TrckGroupAsideGWSelectorFixtureQuery($slug: String!) {
    so5 {
      so5Fixture(slug: $slug) {
        slug
        shortDisplayName
        so5LeaderboardGroups(groupType: COMPETITION) {
          slug
          so5LeagueTrackGroups {
            slug
            so5LeagueTracks {
              slug
              so5Leaderboards {
                slug
                so5LeaderboardType
              }
              entrySo5Leaderboard {
                slug
                so5LeaderboardType
              }
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  TrckGroupAsideGWSelectorFixtureQuery,
  TrckGroupAsideGWSelectorFixtureQueryVariables
>;

type Props = {
  navigateTo: (args: {
    fixture: string;
    competition: string;
    trckGrp: string;
    trck: string;
    leaderboardSlug: string;
  }) => void;
  aasmStates?: string[];
  selectedFixture: TrckGroupAsideGWSelector_so5Fixture;
  so5LeaderboardType: So5LeaderboardType | undefined;
};

export const TrckGroupAsideGWSelector = ({
  navigateTo,
  aasmStates,
  selectedFixture,
  so5LeaderboardType,
}: Props) => {
  const { sport } = useSportContext();
  const [selectedFixtureSlug, setSelectedFixtureSlug] = useState<string>();

  const { data } = useQuery(query, {
    variables: {
      sport,
      aasmStates,
      eventType: selectedFixture.type,
      so5LeaderboardType: so5LeaderboardType || [],
    },
  });

  const { data: fixtureData } = useQuery(fixtureQuery, {
    variables: {
      slug: selectedFixtureSlug || '',
    },
    skip: !selectedFixtureSlug,
  });

  // TODO @gabriel CA : simplify this logic : https://linear.app/sorare/issue/PROFO-2058/web-clean-trckgroupasidegwselector
  // Handle redirection on fixture change
  useEffect(() => {
    let trck;
    let trckGroup;
    let leaderboardSlug;

    if (selectedFixtureSlug === selectedFixture.slug) {
      return;
    }

    // This is using the so5LeaderboardType so we keep the selected scarcity on the redirection
    const newLeaderboardGroup =
      fixtureData?.so5.so5Fixture?.so5LeaderboardGroups.find(
        ({ so5LeagueTrackGroups }) => {
          trckGroup = so5LeagueTrackGroups.find(({ so5LeagueTracks }) => {
            const trckItem = so5LeagueTracks.find(({ entrySo5Leaderboard }) => {
              return (
                entrySo5Leaderboard.so5LeaderboardType === so5LeaderboardType
              );
            });
            leaderboardSlug = trckItem?.so5Leaderboards[0]?.slug;
            trck = trckItem?.slug;
            return trck;
          })?.slug;
          return trckGroup;
        }
      );

    if (selectedFixtureSlug && newLeaderboardGroup) {
      navigateTo({
        fixture: selectedFixtureSlug,
        competition: newLeaderboardGroup.slug,
        trckGrp: trckGroup || '',
        trck: trck || '',
        leaderboardSlug: leaderboardSlug || '',
      });
    }
  }, [
    navigateTo,
    fixtureData,
    selectedFixture,
    selectedFixtureSlug,
    so5LeaderboardType,
  ]);

  const fixtures = data?.so5.so5Fixtures.nodes;

  return (
    <Dropdown
      label={({ open, ...btnProps }) => (
        <span>
          <Button
            {...btnProps}
            size="small"
            color="tertiary"
            disableDebounce
            endIcon={<FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />}
          >
            <Horizontal>
              <FontAwesomeIcon icon={faCalendar} width={12} />
              <LabelM bold>{selectedFixture.shortDisplayName}</LabelM>·
              <LabelM color="var(--c-nd-600)">
                <DateRange
                  startDate={selectedFixture.startDate}
                  endDate={selectedFixture.endDate}
                />
              </LabelM>
            </Horizontal>
          </Button>
        </span>
      )}
    >
      {({ closeDropdown }) => (
        <OptionsWrapper>
          {fixtures?.map(fixture => (
            <DropdownOptionLabel
              key={fixture.slug}
              className={classNames({
                selected: fixture.slug === selectedFixtureSlug,
              })}
              onClick={() => {
                setSelectedFixtureSlug(fixture.slug);
                closeDropdown();
              }}
            >
              <Horizontal>
                <FontAwesomeIcon icon={faCalendar} width={12} />
                <LabelM bold>{fixture.shortDisplayName}</LabelM>
                {selectedFixture.type !== So5FixtureEvent.DAILY && (
                  <>
                    ·
                    <LabelM color="var(--c-nd-600)">
                      {!!fixture && (
                        <DateRange
                          startDate={fixture.startDate}
                          endDate={fixture.endDate}
                        />
                      )}
                    </LabelM>
                  </>
                )}
              </Horizontal>
            </DropdownOptionLabel>
          ))}
        </OptionsWrapper>
      )}
    </Dropdown>
  );
};

TrckGroupAsideGWSelector.fragments = {
  so5Fixture: gql`
    fragment TrckGroupAsideGWSelector_so5Fixture on So5Fixture {
      slug
      type
      gameWeek
      endDate
      startDate
      shortDisplayName
    }
  ` as TypedDocumentNode<TrckGroupAsideGWSelector_so5Fixture>,
};
